'use client';

import React, { useState } from 'react';
import { FileText, Send, CheckCircle2, Phone, Building, Loader2, Sparkles } from 'lucide-react';
import Link from 'next/link';

export default function RequestQuotePage() {
  const [loading, setLoading] = useState(false);
  const [successQuote, setSuccessQuote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    companyName: '',
    taxNumber: '',
    projectTitle: '',
    projectScope: 'Shtëpi Private / Vilë',
    siteAddress: '',
    description: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/quotes/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Dështoi dërgimi i kërkesës.');
      }

      setSuccessQuote(data.quoteNumber);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (successQuote) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center space-y-6">
        <div className="w-16 h-16 rounded-2xl bg-emerald-100 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-10 h-10" />
        </div>
        <h1 className="text-3xl font-black text-slate-900 dark:text-white">Kërkesa Juaj u Dërgua me Sukses!</h1>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Numri i Preventivit të Gjeneruar: <span className="font-mono font-bold text-sky-700 dark:text-sky-300 px-2 py-1 bg-sky-50 dark:bg-sky-950 border border-sky-200 dark:border-sky-800 rounded">{successQuote}</span>
        </p>
        <p className="text-xs text-slate-500 dark:text-slate-400 max-w-md mx-auto">
          Inxhinierët tanë do të shqyrtojnë kërkesën tuaj teknike dhe do t&apos;ju kontaktojnë me një ofertë të plotë të personalizuar dhe skicë termoteknike.
        </p>
        <div className="pt-4 flex justify-center gap-4">
          <Link href="/shop" className="px-5 py-2.5 rounded-xl bg-slate-900 dark:bg-slate-800 text-white text-xs font-bold transition-colors">
            Kthehu te Dyqani
          </Link>
          <Link href="/account/quotes" className="px-5 py-2.5 rounded-xl bg-sky-50 dark:bg-sky-950 text-sky-700 dark:text-sky-300 font-bold text-xs border border-sky-200 dark:border-sky-800 transition-colors">
            Shiko Preventivat e Mia
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-12 w-full">
      <div className="mb-10 text-center max-w-2xl mx-auto space-y-3">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-sky-100 dark:bg-sky-950 text-sky-800 dark:text-sky-300 border border-sky-200 dark:border-sky-800 text-xs font-bold">
          <Sparkles className="w-3.5 h-3.5 text-sky-600 dark:text-sky-400" />
          Konsulencë & Preventiv Falas
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight">
          Kërkoni Preventiv Profesional HVAC
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Për shtëpi, rezidenca me shumë dhoma, pompa nxehtësie, objekte tregtare apo kaskada industriale. Plotësoni formularin e mëposhtëm.
        </p>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-rose-50 dark:bg-rose-950/50 border border-rose-200 dark:border-rose-900 text-rose-700 dark:text-rose-300 text-xs mb-6 font-semibold">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-900 p-6 sm:p-10 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
              Emri dhe Mbiemri *
            </label>
            <input
              type="text"
              required
              value={form.clientName}
              onChange={(e) => setForm({ ...form, clientName: e.target.value })}
              placeholder="psh. Erion Hoxha"
              className="w-full px-3.5 py-2.5 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500 transition-colors"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
              Numri i Telefonit *
            </label>
            <input
              type="tel"
              required
              value={form.clientPhone}
              onChange={(e) => setForm({ ...form, clientPhone: e.target.value })}
              placeholder="+355 69 ..."
              className="w-full px-3.5 py-2.5 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500 transition-colors"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
              Email *
            </label>
            <input
              type="email"
              required
              value={form.clientEmail}
              onChange={(e) => setForm({ ...form, clientEmail: e.target.value })}
              placeholder="emri@kompania.al"
              className="w-full px-3.5 py-2.5 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500 transition-colors"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
              Emri i Biznesit / NIPT (Opsionale për B2B)
            </label>
            <input
              type="text"
              value={form.companyName}
              onChange={(e) => setForm({ ...form, companyName: e.target.value })}
              placeholder="psh. Shpk Shqipëria / M12345678A"
              className="w-full px-3.5 py-2.5 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500 transition-colors"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
              Lloji i Projektit *
            </label>
            <select
              value={form.projectScope}
              onChange={(e) => setForm({ ...form, projectScope: e.target.value })}
              className="w-full px-3.5 py-2.5 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-sky-500 transition-colors"
            >
              <option value="Shtëpi Private / Vilë" className="bg-white dark:bg-slate-800 text-slate-900 dark:text-white">Shtëpi Private / Vilë</option>
              <option value="Apartament" className="bg-white dark:bg-slate-800 text-slate-900 dark:text-white">Apartament</option>
              <option value="Pompë Nxehtësie & Ngrohje Dyshemeje" className="bg-white dark:bg-slate-800 text-slate-900 dark:text-white">Pompë Nxehtësie & Ngrohje Dyshemeje</option>
              <option value="Sistem Multi-Split ose VRF" className="bg-white dark:bg-slate-800 text-slate-900 dark:text-white">Sistem Multi-Split ose VRF</option>
              <option value="Impiant Diellor për Ujë Sanitar" className="bg-white dark:bg-slate-800 text-slate-900 dark:text-white">Impiant Diellor për Ujë Sanitar</option>
              <option value="Objekt Tregtar / Zyra / Hotel" className="bg-white dark:bg-slate-800 text-slate-900 dark:text-white">Objekt Tregtar / Zyra / Hotel</option>
              <option value="Objekt Industrial / Magazinë" className="bg-white dark:bg-slate-800 text-slate-900 dark:text-white">Objekt Industrial / Magazinë</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
              Titulli i Shkurtër i Kërkesës *
            </label>
            <input
              type="text"
              required
              value={form.projectTitle}
              onChange={(e) => setForm({ ...form, projectTitle: e.target.value })}
              placeholder="psh. Ngrohje me pompë nxehtësie vilë 2 katëshe"
              className="w-full px-3.5 py-2.5 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500 transition-colors"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
              Vendndodhja e Objektit (Qyteti, Zona)
            </label>
            <input
              type="text"
              value={form.siteAddress}
              onChange={(e) => setForm({ ...form, siteAddress: e.target.value })}
              placeholder="psh. Elbasan, Lagjja 5 Maji ose Tiranë, Lundër"
              className="w-full px-3.5 py-2.5 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500 transition-colors"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1">
              Përshkrimi i Detajuar i Kërkesës *
            </label>
            <textarea
              required
              rows={4}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Shënoni sipërfaqen në m², numrin e ambienteve, lartësinë e tavanit, izolimin ekzistues apo çdo kërkesë specifike..."
              className="w-full px-3.5 py-2.5 text-xs rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500 transition-colors"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-4 rounded-xl bg-sky-600 hover:bg-sky-500 text-white font-bold text-sm shadow-xl shadow-sky-600/30 flex items-center justify-center gap-2 transition-all disabled:opacity-50 cursor-pointer"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Duke dërguar kërkesën...
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              Dërgo Kërkesën për Preventiv
            </>
          )}
        </button>
      </form>
    </div>
  );
}
